import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { AdNodeManager } from "../../core/AdNodeManager";
import type { SlotConfig, SlotConfigMap } from "../../config/platforms/types";
import { css } from "../../helpers/css";
import { styleSheet } from "../../helpers/styleSheet";

type DynamicSingleSlotConfig = NonNullable<
  NonNullable<SlotConfig["dynamic"]>["single"]
>;
type SlotConfigWithDynamicSingleSlot = SlotConfig & {
  dynamic: {
    single: DynamicSingleSlotConfig;
  };
};

const isDynamicSingleSlotConfig = (
  slotConfig: SlotConfig,
): slotConfig is SlotConfigWithDynamicSingleSlot =>
  typeof slotConfig?.dynamic === "object" && !!slotConfig.dynamic.single;

const isDynamicOutOfPPageSlotConfig = (
  slotConfig: SlotConfig,
): slotConfig is SlotConfigWithDynamicSingleSlot =>
  typeof slotConfig?.dynamic === "object" && !!slotConfig.dynamic.outOfPage;

/**
 * DynamicSlotPlugin is responsible for rendering dynamic slots on the page.
 * It injects the slot div (with the id) into the anchor element specified in the configuration (`selector`).
 * If the configuration specifies a wrapper, it will be created and the slot div will be appended to it.
 * Wrapper can be styled with the `css` property.
 *
 * This is useful for single dynamic slots like floor_adhesion, for repeatable slots use RepeatableSlotsPlugin.
 */
export class DynamicSlotPlugin extends AdEnginePlugin {
  private renderWrapper(id: string, styles: string | undefined) {
    const container = document.createElement("div");
    container.id = id;

    if (styles && styleSheet) {
      styleSheet.insertRule(css`
        #${id} {
          ${styles}
        }
      `);
    }

    return container;
  }

  private handleDynamicSingleSlot(
    slotConfig: SlotConfigWithDynamicSingleSlot,
    adNodeManager: AdNodeManager,
  ) {
    const singleConfig = slotConfig.dynamic.single;
    const anchor = document.querySelector(singleConfig.selector);

    if (!anchor) {
      console.warn(
        `Dynamic slot anchor element with selector "${singleConfig.selector}" not found`,
      );
      return;
    }

    this.renderDynamicSlot(anchor, slotConfig.name, singleConfig);
    adNodeManager.createAdNode(slotConfig.name, slotConfig);
  }

  private handleDynamicOutOfPageSlot(
    slotConfig: SlotConfig,
    adNodeManager: AdNodeManager,
  ) {
    adNodeManager.createAdNode(slotConfig.name, slotConfig);
  }

  private renderDynamicSlot(
    anchor: Element,
    id: string,
    singleConfig: DynamicSingleSlotConfig,
  ) {
    const slotElement = document.createElement("div");
    slotElement.id = id;

    if (singleConfig.wrapper?.id) {
      const wrapper = this.renderWrapper(
        singleConfig.wrapper.id,
        singleConfig.wrapper.css,
      );

      wrapper.appendChild(slotElement);
      anchor[singleConfig.insertMethod](wrapper);

      return;
    }

    anchor[singleConfig.insertMethod](slotElement);
  }

  hooks: Hooks = {
    onAfterAdNodeManagerInit: (
      adNodeManager: AdNodeManager,
      slotConfigMap: SlotConfigMap["baseSlots"],
    ) => {
      Object.values(slotConfigMap).forEach((slotConfig) => {
        try {
          if (isDynamicSingleSlotConfig(slotConfig)) {
            this.handleDynamicSingleSlot(slotConfig, adNodeManager);
          }
          if (isDynamicOutOfPPageSlotConfig(slotConfig)) {
            this.handleDynamicOutOfPageSlot(slotConfig, adNodeManager);
          }
        } catch (error) {
          console.error(
            `Dynamic slot "${slotConfig.name}" failed to render:`,
            error,
          );
        }
      });
    },
  };
}
